import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './index.css';
import { AuthProvider, useAuth } from './context/AuthContext';
import { CartProvider } from './context/CartContext';
import { WishlistProvider } from './context/WishlistContext';
import { LocationProvider } from './context/LocationContext';
import { BrandingProvider } from './context/BrandingContext';

import Home from './pages/Home';
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import Register from './pages/Register';
import OwnerRegister from './pages/OwnerRegister';
import CustomerDashboard from './pages/CustomerDashboard';
import OwnerDashboard from './pages/OwnerDashboard';
import AdminDashboard from './pages/AdminDashboard';
import ShopList from './pages/ShopList';
import ShopDetail from './pages/ShopDetail';
import MaterialDetail from './pages/MaterialDetail';
import Cart from './pages/Cart';
import Orders from './pages/Orders';
import OwnerMaterials from './pages/OwnerMaterials';
import Wishlist from './pages/Wishlist';
import StaticPage from './pages/StaticPage';
import MaterialCalculator from './pages/MaterialCalculator';
import Footer from './components/Footer';
import { playNotificationSound } from './utils/notificationSound';

const ProtectedRoute = ({ children, roles }) => {
  const { user, loading } = useAuth();
  if (loading) return <div style={{ display:'flex', justifyContent:'center', alignItems:'center', height:'100vh' }}><div className="spinner" /></div>;
  if (!user) return <Navigate to="/login" />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/" />;
  return children;
};

const ScrollToTop = () => {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    if (hash) {
      const timer = setTimeout(() => {
        const target = document.getElementById(hash.slice(1));
        if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 0);
      return () => clearTimeout(timer);
    }
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [pathname, hash]);

  return null;
};

const AppFooter = () => {
  const { pathname } = useLocation();
  const hideFooterPaths = ['/login', '/forgot-password', '/register', '/register/owner'];
  if (hideFooterPaths.includes(pathname)) return null;
  return <Footer />;
};

const AppRoutes = () => (
  <Routes>
    <Route path="/" element={<Home />} />
    <Route path="/login" element={<Login />} />
    <Route path="/forgot-password" element={<ForgotPassword />} />
    <Route path="/register" element={<Register />} />
    <Route path="/register/owner" element={<OwnerRegister />} />
    <Route path="/shops" element={<ShopList />} />
    <Route path="/shops/:id" element={<ShopDetail />} />
    <Route path="/materials/:id" element={<MaterialDetail />} />
    <Route path="/wishlist" element={<Wishlist />} />
    <Route path="/about" element={<StaticPage type="about" />} />
    <Route path="/contact" element={<StaticPage type="contact" />} />
    <Route path="/help" element={<StaticPage type="help" />} />
    <Route path="/calculator" element={<MaterialCalculator />} />
    <Route path="/privacy" element={<StaticPage type="privacy" />} />
    <Route path="/terms" element={<StaticPage type="terms" />} />
    <Route path="/cart" element={<ProtectedRoute roles={['customer']}><Cart /></ProtectedRoute>} />
    <Route path="/orders" element={<ProtectedRoute roles={['customer']}><Orders /></ProtectedRoute>} />
    <Route path="/dashboard" element={<ProtectedRoute roles={['customer']}><CustomerDashboard /></ProtectedRoute>} />
    <Route path="/owner/dashboard" element={<ProtectedRoute roles={['owner']}><OwnerDashboard /></ProtectedRoute>} />
    <Route path="/owner/materials" element={<ProtectedRoute roles={['owner']}><OwnerMaterials /></ProtectedRoute>} />
    <Route path="/admin" element={<ProtectedRoute roles={['admin']}><AdminDashboard /></ProtectedRoute>} />
    <Route path="*" element={<Navigate to="/" />} />
  </Routes>
);

export default function App() {
  return (
    <AuthProvider>
      <BrandingProvider>
        <LocationProvider>
          <CartProvider>
            <WishlistProvider>
              <BrowserRouter>
                <ScrollToTop />
                <AppRoutes />
                <AppFooter />
                <ToastContainer
                  position="top-right"
                  autoClose={2500}
                  hideProgressBar={false}
                  theme="colored"
                  onOpen={(props) => playNotificationSound(props?.type)}
                />
              </BrowserRouter>
            </WishlistProvider>
          </CartProvider>
        </LocationProvider>
      </BrandingProvider>
    </AuthProvider>
  );
}
